<script setup>
// 分享加碼彈窗。
//
// 視覺與每日提醒彈窗（`DailyVoteDialog.vue`）是同一套：同樣的遮罩、圓角、天空到草地的
// 漸層、雲與山丘、黃筆刷大標、VOTE 票券與橘色膠囊按鈕，只是人物換一位、多一列社群圖示。
// **改了一邊的視覺就回頭看另一邊**，兩個彈窗擺在一起要像同一套。
//
// 流程上它只是插在中間的一道確認：步道詳情的分享鈕按下去先開這裡，
// 按下按鈕才真的去分享（開視窗／系統面板／複製連結）並回報加碼——那些踩過的坑
// 仍在 `composables/useShare.js`，這裡一概不碰。
import shareLineIcon from '~/assets/img/share-line.png'
import shareFbIcon from '~/assets/img/share-facebook.png'
import shareLinkIcon from '~/assets/img/share-link.png'
import hillImg from '~/assets/img/hill.png'
import hikerImg from '~/assets/img/vote-hiker-right.png'
import cloudImg from '~/assets/img/cloud-2.png'
import arrowIcon from '~/assets/img/icon-arrow.png'

// 分享一次，四區各再加一張票（後端 SHARE_BONUS_PER_ZONE × 四區，見 CLAUDE.md 5.18）
const SHARE_VOTES = 4

const props = defineProps({
  open: { type: Boolean, default: false },
  // 使用者按的是哪一顆分享鈕：line / facebook / link
  platform: { type: String, default: '' },
  // 這次按下去到底加不加得到票：ready 加得到；guest 未登入；claimed 今天領過了；off 非活動期間。
  // 加不到還照樣讓人分享（分享本來就不只為了票），但話不能說成「你可額外獲得 4 張投票券」
  state: { type: String, default: 'ready' }
})

const emit = defineEmits(['close', 'confirm'])

const { lockScroll } = useScrollLock()

// 圖示與順序跟詳情頁的分享列一致，按到的那一顆亮起來，其餘壓暗——
// 按下「領票」之後會跳去哪，看一眼就知道
const channels = [
  { key: 'line', label: 'LINE', icon: shareLineIcon },
  { key: 'facebook', label: 'Facebook', icon: shareFbIcon },
  { key: 'link', label: '複製連結', icon: shareLinkIcon }
]

const DESC = {
  ready: `感謝你分享活動至社群平台！<br />你可額外獲得 <b>${SHARE_VOTES}</b> 張投票券！`,
  guest: `登入後分享活動至社群平台，<br />就能額外獲得 ${SHARE_VOTES} 張投票券！`,
  claimed: '今天的分享加碼已經領過了，<br />仍歡迎你把臺灣步道分享給更多山友！',
  off: '目前沒有進行中的票選活動，<br />分享暫時不加碼，仍歡迎你分享給更多山友！'
}

const desc = computed(() => DESC[props.state] || DESC.ready)
// 領不到票就不要喊「領票」
const btnText = computed(() => (props.state === 'ready' ? '點我進行領票' : '前往分享'))

function close() {
  emit('close')
}

function onKeydown(e) {
  if (e.key === 'Escape') close()
}

watch(
  () => props.open,
  (open) => {
    if (open) {
      window.addEventListener('keydown', onKeydown)
      lockScroll(true)
      return
    }
    window.removeEventListener('keydown', onKeydown)
    lockScroll(false)
  }
)

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKeydown)
})
</script>

<template>
  <ClientOnly>
    <Teleport to="body">
      <Transition name="sb">
        <div v-if="open" class="sb-mask" @click.self="close()">
          <div class="sb-panel" role="dialog" aria-modal="true" aria-labelledby="sb-title">
            <!-- 風景是背景，不進無障礙樹 -->
            <div class="sb-scene" aria-hidden="true">
              <img class="sb-cloud sb-cloud-a" :src="cloudImg" alt="" />
              <img class="sb-cloud sb-cloud-b" :src="cloudImg" alt="" />
              <img class="sb-hill" :src="hillImg" alt="" />
              <img class="sb-hiker" :src="hikerImg" alt="" />
            </div>

            <button class="sb-close" type="button" aria-label="關閉" @click="close()">
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6 6 L18 18 M18 6 L6 18" />
              </svg>
            </button>

            <div class="sb-body">
              <!-- 拆成兩句：窄螢幕一行放不下，讓它在句讀處斷，不要斷在「步道」中間 -->
              <p class="sb-kicker">
                <span>分享你的山林時光，</span><span>讓更多人看見臺灣步道！</span>
              </p>

              <h2 id="sb-title" class="sb-title">
                分享加碼<b>{{ SHARE_VOTES }}</b
                >張票
              </h2>

              <!-- 文案依狀態換，數字要強調，所以走 v-html（內容是上面寫死的常數） -->
              <p class="sb-desc" v-html="desc"></p>

              <ul class="sb-channels">
                <li
                  v-for="c in channels"
                  :key="c.key"
                  class="sb-channel"
                  :class="{ 'is-on': c.key === platform }"
                >
                  <img :src="c.icon" width="44" height="44" :alt="c.label" />
                </li>
              </ul>

              <!-- 票券張數跟著加碼票走，改了常數版面自己對得上 -->
              <ul class="sb-tickets">
                <li v-for="i in SHARE_VOTES" :key="i" class="sb-ticket" :style="{ '--i': i - 1 }">
                  <svg class="sb-ticket-art" viewBox="0 0 44 26" aria-hidden="true">
                    <path d="M1 25 L13 4 L23 18 L28 11 L43 25 Z" fill="#8cbb6a" />
                    <path d="M13 4 L23 18 L16 25 L1 25 Z" fill="#5f9642" />
                  </svg>
                  <span class="sb-ticket-line"></span>
                  <span class="sb-ticket-text">VOTE</span>
                </li>
              </ul>

              <button class="sb-cta" type="button" @click="emit('confirm')">
                {{ btnText }}
                <img class="sb-cta-arrow" :src="arrowIcon" alt="" width="22" height="15" />
              </button>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
  </ClientOnly>
</template>

<style scoped>
.sb-mask {
  position: fixed;
  inset: 0;
  /* 與投票確認彈窗同層：都是使用者自己按出來、按了才會往下走的確認窗 */
  z-index: 210;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(0, 0, 0, 0.5);
}

.sb-panel {
  position: relative;
  width: 100%;
  max-width: 560px;
  max-height: calc(100vh - 48px);
  overflow: hidden auto;
  border-radius: 28px;
  /* 天色由上而下：淡藍綠 → 草地（與每日提醒彈窗同一組） */
  background: linear-gradient(180deg, #f7fbf3 0%, #eef7e7 46%, #e2f0d8 100%);
  box-shadow: 0 28px 70px rgba(var(--site-shadow-rgb), 0.32);
  font-family: var(--site-font);
  text-align: center;
}

/* ---------------- 背景風景 ---------------- */

.sb-scene {
  position: absolute;
  inset: 0;
  pointer-events: none;
  user-select: none;
}

.sb-cloud {
  position: absolute;
  display: block;
  opacity: 0.75;
}

.sb-cloud-a {
  top: 58px;
  left: -20px;
  width: 116px;
}

.sb-cloud-b {
  top: 26px;
  right: -26px;
  width: 92px;
  opacity: 0.55;
}

/* 山丘與登山客貼齊彈窗底緣；這一張人物朝右，擺左邊剛好看向內容 */
.sb-hill {
  position: absolute;
  right: -10%;
  bottom: -10px;
  display: block;
  width: 88%;
  opacity: 0.88;
}

.sb-hiker {
  position: absolute;
  bottom: 0;
  left: 8px;
  display: block;
  width: 88px;
}

/* ---------------- 關閉 ---------------- */

.sb-close {
  position: absolute;
  top: 16px;
  right: 16px;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.32);
  cursor: pointer;
  transition: background-color 0.25s;
}

.sb-close:hover {
  background: rgba(0, 0, 0, 0.5);
}

.sb-close svg {
  width: 22px;
  height: 22px;
  fill: none;
  stroke: #fff;
  stroke-width: 2.4;
  stroke-linecap: round;
}

/* ---------------- 內容 ---------------- */

.sb-body {
  position: relative;
  z-index: 1;
  padding: 44px 36px 36px;
}

/* 兩側的斜線是稿上的手寫裝飾，用 CSS 畫，不另外切圖 */
.sb-kicker {
  display: inline-flex;
  align-items: center;
  margin: 0;
  color: #2f6b34;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 1px;
}

/* 間距掛在斜線上而不是 flex 的 gap：gap 會連兩句中間也撐開一格 */
.sb-kicker::before,
.sb-kicker::after {
  content: '';
  width: 18px;
  height: 18px;
  border-right: 3px solid #5f9642;
  border-left: 3px solid #5f9642;
  transform: skewX(-20deg);
}

.sb-kicker::before {
  margin-right: 14px;
}

.sb-kicker::after {
  margin-left: 14px;
}

.sb-title {
  position: relative;
  display: inline-block;
  margin: 8px 0 0;
  padding: 0 6px;
  color: #1f5132;
  font-size: clamp(36px, 7.6vw, 54px);
  font-weight: 900;
  line-height: 1.24;
  letter-spacing: 2px;
}

/* 標題底下的黃色筆刷：壓在字後面，略帶傾斜才像手繪 */
.sb-title::after {
  content: '';
  position: absolute;
  right: 0;
  bottom: 6px;
  left: 0;
  z-index: -1;
  height: 14px;
  border-radius: 999px;
  background: #ffdf7e;
  transform: rotate(-0.8deg);
}

.sb-title b {
  margin: 0 4px;
  color: var(--site-orange);
  font-size: 1.28em;
  font-weight: 900;
}

.sb-desc {
  margin: 12px 0 0;
  color: var(--site-text);
  font-size: 16px;
  line-height: 1.75;
}

.sb-desc :deep(b) {
  color: var(--site-orange);
  font-size: 1.1em;
}

/* ---------------- 社群圖示 ---------------- */

.sb-channels {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin: 18px 0 0;
  padding: 0;
  list-style: none;
}

/* 按到的那一顆放大並圈起來，其餘壓暗——等一下會跳去哪一家，一眼看得出來 */
.sb-channel {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  opacity: 0.38;
  transition:
    opacity 0.25s,
    transform 0.25s;
}

.sb-channel.is-on {
  opacity: 1;
  transform: scale(1.14);
  box-shadow:
    0 0 0 3px #fff,
    0 0 0 6px rgba(var(--site-orange-rgb), 0.55);
}

.sb-channel img {
  display: block;
  width: 44px;
  height: 44px;
}

/* ---------------- VOTE 票券 ---------------- */

.sb-tickets {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 24px 0 0;
  padding: 0;
  list-style: none;
}

/* 四張票交錯歪一點，像隨手擺上去的；票根兩側的半圓缺口用漸層挖 */
.sb-ticket {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 74px;
  height: 94px;
  border-radius: 12px;
  background:
    radial-gradient(circle at 0 58px, transparent 6px, #fdf7e8 6.5px),
    radial-gradient(circle at 100% 58px, transparent 6px, #fdf7e8 6.5px);
  box-shadow: 0 8px 18px rgba(var(--site-shadow-rgb), 0.16);
  transform: rotate(calc((var(--i) - 1.5) * 4deg));
}

.sb-ticket-art {
  width: 42px;
  height: 25px;
}

.sb-ticket-line {
  width: 44px;
  border-top: 2px dashed #cbbf9c;
}

.sb-ticket-text {
  color: #4f8a3a;
  font-size: 13px;
  font-weight: 900;
  letter-spacing: 1px;
}

/* ---------------- 按鈕 ---------------- */

.sb-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  height: 62px;
  margin: 26px 0 0;
  padding: 0 40px;
  border: 0;
  border-radius: 999px;
  background: var(--site-orange);
  color: #fff;
  font-family: inherit;
  font-size: 23px;
  font-weight: 900;
  letter-spacing: 2px;
  box-shadow: 0 14px 26px rgba(var(--site-orange-rgb), 0.34);
  cursor: pointer;
  transition:
    transform 0.28s cubic-bezier(0.22, 0.61, 0.36, 1),
    background-color 0.28s,
    box-shadow 0.28s;
}

.sb-cta:hover {
  background: var(--site-orange-deep);
  transform: translateY(-3px);
  box-shadow: 0 20px 34px rgba(var(--site-orange-rgb), 0.44);
}

.sb-cta-arrow {
  display: block;
  transition: transform 0.28s cubic-bezier(0.22, 0.61, 0.36, 1);
}

.sb-cta:hover .sb-cta-arrow {
  transform: translateX(6px);
}

/* ---------------- 進出場（與登入、投票彈窗同一套） ---------------- */

.sb-enter-active,
.sb-leave-active {
  transition: opacity 0.25s;
}

.sb-enter-from,
.sb-leave-to {
  opacity: 0;
}

.sb-enter-active .sb-panel,
.sb-leave-active .sb-panel {
  transition: transform 0.25s;
}

.sb-enter-from .sb-panel,
.sb-leave-to .sb-panel {
  transform: translateY(16px) scale(0.97);
}

@media (prefers-reduced-motion: reduce) {
  .sb-enter-active,
  .sb-leave-active,
  .sb-enter-active .sb-panel,
  .sb-leave-active .sb-panel,
  .sb-cta,
  .sb-cta-arrow,
  .sb-close,
  .sb-channel {
    transition: none;
  }
}

@media (max-width: 560px) {
  .sb-mask {
    padding: 16px;
  }

  .sb-panel {
    border-radius: 22px;
  }

  /* 上緣留給關閉鈕：版面一窄，上一行的斜線裝飾就會頂到它 */
  .sb-body {
    padding: 50px 20px 28px;
  }

  /* 稿上的斷句是照桌機寬度分的，窄螢幕讓它自己折 */
  .sb-desc :deep(br) {
    display: none;
  }

  /* 窄螢幕沒有登山客擋著，山丘整座露出來，往下壓才不會頂到按鈕 */
  .sb-hill {
    right: -14%;
    bottom: -18px;
    width: 86%;
  }

  .sb-hiker {
    display: none;
  }

  .sb-cloud-a {
    width: 88px;
  }

  /* 兩行的時候斜線擺哪裡都不對，窄螢幕收起來；兩句各佔一行（flex 容器要改方向才分得開） */
  .sb-kicker {
    flex-direction: column;
    font-size: 13px;
    line-height: 1.5;
  }

  .sb-kicker::before,
  .sb-kicker::after {
    display: none;
  }

  .sb-desc {
    font-size: 14px;
  }

  .sb-channels {
    gap: 12px;
    margin-top: 14px;
  }

  .sb-tickets {
    gap: 6px;
    margin-top: 18px;
  }

  .sb-ticket {
    width: 60px;
    height: 78px;
    gap: 6px;
    background:
      radial-gradient(circle at 0 48px, transparent 5px, #fdf7e8 5.5px),
      radial-gradient(circle at 100% 48px, transparent 5px, #fdf7e8 5.5px);
  }

  .sb-ticket-art {
    width: 34px;
    height: 20px;
  }

  .sb-ticket-line {
    width: 34px;
  }

  .sb-ticket-text {
    font-size: 12px;
  }

  .sb-cta {
    height: 52px;
    margin-top: 22px;
    padding: 0 26px;
    font-size: 18px;
  }
}
</style>
